import { CustomPlanModal } from "#web/components/billing/CustomPlanModal";
import PlanCard from "#web/components/billing/PlanCard";
import { SocialProof } from "#web/components/billing/SocialProof/SocialProof";
import { SpinnerCentered } from "#web/components/common/SpinnerCentered/SpinnerCentered";
import { makeShop, ShopQuery } from "#web/components/shop/queries/shopQuery";
import { subscriptionPlans } from "#web/data/subscriptionPlans";
import { api } from "#web/services/api";
import { i18nLike } from "#web/services/i18nLike";
import { useAction } from "@gadgetinc/react";
import { Banner, BlockStack, Box, Button, InlineGrid, InlineStack, Page, Text } from "@shopify/polaris";
import { DiscountCodeIcon, LiveIcon } from "@shopify/polaris-icons";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

const en = {
  error: {
    description: "Please try again later. If the issue persists, please contact support.",
    title: "Error loading page",
  },
  partnerSetup: "Partner setup",
  title: "Billing",
};

export const Billing = () => {
  const t = i18nLike(en);
  const navigate = useNavigate();
  const [clickedPlan, setClickedPlan] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const toggleModal = useCallback(() => {
    setModalOpen((open) => !open);
  }, []);
  const { data: shopData, isError: shopError, isFetching: shopFetching } = useQuery(ShopQuery());
  const shop = shopData ? makeShop(shopData) : null;

  const [, subscribe] = useAction(api.shopifyShop.subscribe, {
    select: {
      confirmationUrl: true,
      id: true,
      subscription: {
        type: true,
      },
    },
  });
  const subscribeTo = async (planId: string) => {
    setClickedPlan(true);
    const response = await subscribe({
      id: shop?.id ?? "",
      planId: parseInt(planId),
    });

    if (response.error) {
      window.alert("There was a error with your request. Please contact us.");
    } else if (response.data?.confirmationUrl && window.top) {
      window.top.location.href = response.data.confirmationUrl;
    } else {
      navigate("/");
    }
    setClickedPlan(false);
  };

  const conditionalBackAction = () => {
    if (shop?.subscription) {
      return {
        content: "Dashboard",
        onAction: () => {
          navigate("/");
        },
      };
    }

    return undefined;
  };

  if (shopError) {
    return (
      <Page>
        <Banner title={t("error.title")} tone="critical">
          {t("error.description")}
        </Banner>
      </Page>
    );
  }

  return (
    <Page
      backAction={conditionalBackAction()}
      secondaryActions={
        <Button onClick={toggleModal} variant="tertiary">
          {t("partnerSetup")}
        </Button>
      }
      title={t("title")}
    >
      {shopFetching ? (
        <SpinnerCentered />
      ) : (
        <>
          <CustomPlanModal onClose={toggleModal} open={modalOpen} subscribeTo={subscribeTo} />
          <InlineGrid columns="4" gap="200">
            <PlanCard
              crossedFeatures={["Extension Blocking", "Duplicates Blocking"]}
              description="Daily leak alerts"
              features={["Daily scanning of 10+ major coupon leak sites", "Protected Links", "Live support"]}
              icon={LiveIcon}
              loading={clickedPlan}
              onAction={() => {
                void subscribeTo(String(subscriptionPlans.leaked.id));
              }}
              price={String(subscriptionPlans.leaked.price)}
              title={subscriptionPlans.leaked.name}
            />
            <PlanCard
              description="Block coupon extensions"
              disabled={shop!.planName !== "shopify_plus"}
              features={[
                "Everything in Leaks",
                /* @ts-expect-error Type Element is not assignable to type string */
                <p>
                  <b>Up to 5,000 orders</b> per month protected
                </p>,
                "Extension Blocking",
                "Duplicates Blocking",
                "Priority Support",
              ]}
              icon={DiscountCodeIcon}
              loading={clickedPlan}
              onAction={() => {
                void subscribeTo(String(subscriptionPlans.blocking.starter.id));
              }}
              price={String(subscriptionPlans.blocking.starter.price)}
              title={subscriptionPlans.blocking.starter.name}
            />
            <PlanCard
              description="Block coupon extensions"
              disabled={shop!.planName !== "shopify_plus"}
              features={[
                "Everything in Starter",
                /* @ts-expect-error Type Element is not assignable to type string */
                <p>
                  <b>Up to 10,000 orders</b> per month protected
                </p>,
                "Priority Support",
              ]}
              icon={DiscountCodeIcon}
              loading={clickedPlan}
              onAction={() => {
                void subscribeTo(String(subscriptionPlans.blocking.growth.id));
              }}
              price={String(subscriptionPlans.blocking.growth.price)}
              title={subscriptionPlans.blocking.growth.name}
            />
            <PlanCard
              /* @ts-expect-error Type Element is not assignable to type string */
              badge={
                <>
                  <b>More than 40k orders?</b> <br /> Contact support@keepcart.co
                </>
              }
              description="Block coupon extensions"
              disabled={shop!.planName !== "shopify_plus"}
              features={[
                "Everything in Growth",
                /* @ts-expect-error Type Element is not assignable to type string */
                <p>
                  <b>10,000+ or more orders</b> per month protected
                </p>,
                "Priority Support",
              ]}
              icon={DiscountCodeIcon}
              loading={clickedPlan}
              onAction={() => {
                void subscribeTo(String(subscriptionPlans.blocking.pro.id));
              }}
              price={String(subscriptionPlans.blocking.pro.price)}
              title={subscriptionPlans.blocking.pro.name}
            />
          </InlineGrid>
          <Box paddingBlockStart="400">
            <InlineStack align="center">
              <Button
                onClick={() => {
                  void subscribeTo(String(subscriptionPlans.freemium.id));
                }}
                variant="tertiary"
              >
                {/* @ts-expect-error Type Element is not assignable to type string */}
                <BlockStack gap="150">
                  <Text as="span">Continue with free plan</Text>
                  <Text as="span" tone="subdued" variant="bodyXs">
                    (1 coupon leak scan per month. Does not include Extension Blocking)
                  </Text>
                </BlockStack>
              </Button>
            </InlineStack>
          </Box>
          <SocialProof />
        </>
      )}
    </Page>
  );
};
